import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, ChevronRight, Copy } from "lucide-react";
import QRCodeReact from "qrcode.react";
import { toast } from "sonner";
import { BaseSheet } from "@/components/common/BaseSheet";
import { CopyEmailButton } from "@/components/common/CopyableEmail";
import { cn } from "@/lib/utils";
import { useIsMobile } from "@/hooks/ui/useIsMobile";
import { trackCallClick } from "@/lib/ga4";
import { isTelCapable, stripTel } from "@/lib/tel";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";
import type { GenieIconName } from "@/components/ui/genie-icons/genie-icons";

export type ConnectChannelKey = "voice" | "chat" | "phone" | "email" | "qr";

export interface ConnectChooserProps {
  brandColor: string;
  secondaryColor?: string | null;
  /** Same handler as the page's own "Start Conversation" control. */
  onStartVoice: () => void;
  /** Same handler as the existing "Type instead" / text-chat entry point. */
  onStartChat: () => void;
  /** The tenant's own contact number, from `agent.phone.number`. Omit to hide the chip. */
  phoneNumber?: string | null;
  /** The Genie's mailbox address, from `agent.mailbox?.email_address`. Omit to hide the chip. */
  emailAddress?: string | null;
  /** The current live-agent URL — source for the QR code and "Copy link". */
  pageUrl: string;
  /** Optional logo dropped into the QR code's center. */
  qrLogoUrl?: string | null;
  /** Disables every chip (e.g. while memory-consent is still saving). */
  disabled?: boolean;
  /**
   * "strip" (default) — one row of pill chips, every channel equal. Used in the
   * slim band above the orb on the start screen.
    * "gate" — voice and text entry, with contact and sharing tools disclosed
    * separately so they don't compete with starting a conversation.
   */
  variant?: "strip" | "gate";
  /** Optional content between welcome entry actions and secondary contacts. */
  afterEntry?: React.ReactNode;
  /** Narrow-viewport layout for the strip: smaller chips in a horizontally scrollable row. */
  compact?: boolean;
  /**
   * Tab mode (strip variant only): the currently selected channel, painted
   * with a filled brand tint + stronger border. Omit to keep the plain
   * "action row" look (no chip reads as selected).
   */
  activeChannel?: ConnectChannelKey | null;
  /**
   * Tab mode (strip variant only): when provided, Voice / Chat / Phone / Email
   * become tabs that report the chosen channel here instead of acting
   * immediately (dialling `tel:`, opening `mailto:`, etc.) — the caller swaps
   * the stage beneath. Voice still calls `onStartVoice` too, since choosing
   * that tab starts the conversation. QR is unaffected — it stays a popover.
   */
  onSelectChannel?: (channel: ConnectChannelKey) => void;
  className?: string;
}

const copyToClipboard = async (value: string, message: string) => {
  try {
    await navigator.clipboard.writeText(value);
    toast.success(message);
    return true;
  } catch {
    toast.error("Couldn't copy — try again");
    return false;
  }
};

interface ShellProps {
  compact?: boolean;
  disabled?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  href?: string;
  brandColor: string;
  end: string;
  genieIcon: GenieIconName;
  label: string;
  detail?: string;
  trailing?: React.ReactNode;
  /** Tab mode: paints this chip as the active tab (filled brand tint + stronger border). */
  selected?: boolean;
  "aria-label"?: string;
}

/** Strip chrome — icon tile + label(+detail) in a pill, as either a link or a button. */
const ChipShell = ({
  compact,
  disabled,
  onClick,
  href,
  brandColor,
  end,
  genieIcon,
  label,
  detail,
  trailing,
  selected,
  ...rest
}: ShellProps) => {
  const iconTile = (
    <span
      className="flex flex-shrink-0 items-center justify-center rounded-full"
      style={{
        width: compact ? 30 : 34,
        height: compact ? 30 : 34,
        background: `linear-gradient(135deg, ${brandColor}1a, ${end}1a)`,
      }}
    >
      <GenieIcon name={genieIcon} size={compact ? 14 : 16} color={brandColor} secondaryColor={end} gradientEnabled />
    </span>
  );

  const text = !compact && (
    <span className="flex min-w-0 flex-col items-start leading-tight">
      <span className="text-[12.5px] font-semibold text-[#222B45]">{label}</span>
      {detail && <span className="max-w-[130px] truncate text-[11px] text-[#7C8B9C]">{detail}</span>}
    </span>
  );

  const className =
    "group flex flex-shrink-0 items-center gap-2 rounded-full text-left transition-all hover:-translate-y-[1px] hover:shadow-[0_8px_20px_-14px_rgba(15,23,42,0.35)] disabled:cursor-not-allowed disabled:opacity-50" +
    (compact ? " px-2.5 py-2" : " px-3 py-1.5") +
    (selected ? "" : " border border-[#E9EDF3] bg-white");

  const style: React.CSSProperties | undefined = selected
    ? {
        background: `linear-gradient(135deg, ${brandColor}1f, ${end}1f)`,
        border: `1.5px solid ${brandColor}66`,
      }
    : undefined;

  const body = (
    <>
      {iconTile}
      {text}
      {trailing}
    </>
  );

  if (href) {
    return (
      <a
        href={disabled ? undefined : href}
        aria-disabled={disabled}
        aria-current={selected ? "true" : undefined}
        onClick={onClick}
        className={className}
        style={style}
        aria-label={rest["aria-label"] || label}
        title={compact ? label : undefined}
      >
        {body}
      </a>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      className={className}
      style={style}
      aria-label={rest["aria-label"] || label}
      title={compact ? label : undefined}
    >
      {body}
    </button>
  );
};

interface TabPillProps {
  disabled?: boolean;
  onClick?: (e: React.MouseEvent) => void;
  brandColor: string;
  end: string;
  genieIcon: GenieIconName;
  label: string;
  detail?: string;
  selected: boolean;
  /** The channel this tab reports — read by the tablist's arrow-key handler. */
  channelKey: ConnectChannelKey;
  "aria-label"?: string;
}

/**
 * Tab chrome — icon tile + label (+ detail above ~1000px) as a real ARIA tab,
 * never icon-only. Desktop renders a labelled pill; below 768px it collapses
 * to a stacked icon-over-label tile in a horizontally scrollable row. Sizing
 * is pure CSS (Tailwind breakpoints), not a JS `compact` flag, so it tracks
 * the actual viewport regardless of which layout branch is mounted.
 */
const TabPill = ({ disabled, onClick, brandColor, end, genieIcon, label, detail, selected, channelKey, ...rest }: TabPillProps) => {
  const selectedStyle: React.CSSProperties = selected
    ? { backgroundColor: `${brandColor}24`, borderColor: brandColor }
    : { borderColor: "#E9EDF3" };

  return (
    <button
      type="button"
      role="tab"
      aria-selected={channelKey === "qr" ? false : selected}
      data-channel={channelKey}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "group flex w-[66px] flex-shrink-0 snap-start flex-col items-center justify-center gap-1 rounded-2xl border bg-white px-1.5 py-2 text-center transition-all hover:-translate-y-[1px] hover:shadow-[0_8px_20px_-14px_rgba(15,23,42,0.35)] disabled:cursor-not-allowed disabled:opacity-50",
        "md:w-auto md:flex-row md:justify-start md:gap-2.5 md:rounded-full md:py-2.5 md:pl-2.5 md:pr-4 md:text-left",
        selected ? "border-[1.5px]" : "border",
      )}
      style={selectedStyle}
      aria-label={rest["aria-label"] || label}
    >
      <span
        className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full md:h-9 md:w-9"
        style={{ background: `linear-gradient(135deg, ${brandColor}1a, ${end}1a)` }}
      >
        {/* Two sizes (22px mobile / 20px desktop) toggled by a wrapper span —
            GenieIcon sets `display` inline, which would
            stomp a `hidden`/`block` class passed straight to the icon. */}
        <span className="md:hidden">
          <GenieIcon name={genieIcon} size={22} color={brandColor} secondaryColor={end} gradientEnabled />
        </span>
        <span className="hidden md:block">
          <GenieIcon name={genieIcon} size={20} color={brandColor} secondaryColor={end} gradientEnabled />
        </span>
      </span>
      <span className="flex min-w-0 flex-col items-center leading-tight md:items-start">
        <span className="text-[11px] font-semibold md:text-[13.5px]" style={{ color: selected ? brandColor : "#222B45" }}>
          {label}
        </span>
        {detail && (
          <span className="hidden max-w-[150px] truncate text-[11px] text-[#64748B] min-[1000px]:block">{detail}</span>
        )}
      </span>
    </button>
  );
};

/**
 * Gate chrome — the same channel as an interactive card for the two-up grid
 * under the voice hero. Tall enough to be a comfortable thumb target, wide
 * enough that a phone number or email address reads without truncating.
 */
const CardShell = ({ disabled, onClick, href, brandColor, end, genieIcon, label, detail, trailing, ...rest }: ShellProps) => {
  const className =
    "group flex min-h-[64px] w-full items-center gap-3 rounded-2xl border border-genie-border bg-white px-3.5 py-3 text-left shadow-sm transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-genie-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

  const body = (
    <>
      <span
        className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl"
        style={{ background: `linear-gradient(135deg, ${brandColor}1a, ${end}1a)` }}
      >
        <GenieIcon name={genieIcon} size={18} color={brandColor} secondaryColor={end} gradientEnabled />
      </span>
      <span className="flex min-w-0 flex-1 flex-col leading-tight">
        <span className="text-sm font-semibold text-[#222B45]">{label}</span>
        {detail && <span className="mt-0.5 break-all text-xs text-gray-500">{detail}</span>}
      </span>
      {trailing}
    </>
  );

  if (href) {
    return (
      <a
        href={disabled ? undefined : href}
        aria-disabled={disabled}
        onClick={onClick}
        className={className}
        aria-label={rest["aria-label"] || label}
      >
        {body}
      </a>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={className}
      aria-label={rest["aria-label"] || label}
    >
      {body}
    </button>
  );
};

/**
 * "How would you like to connect?" — Voice / Chat / Phone / Email / QR as the
 * single source of truth for every entry point into a Genie conversation.
 * Shared by the welcome gate (`variant="gate"`, voice as the hero card) and the
 * pre-call strip above the orb on the live-agent start screen
 * (`variant="strip"`, one pill row). A channel with no data (no phone, no
 * mailbox) is simply omitted — the row never shows a dead chip.
 *
 * Phone and email each carry their own inline reveal (copy affordance) rather
 * than routing through a separate drawer, so the whole decision stays a single
 * tap on every device.
 */
export const ConnectChooser = ({
  brandColor,
  secondaryColor,
  onStartVoice,
  onStartChat,
  phoneNumber,
  emailAddress,
  pageUrl,
  qrLogoUrl,
  disabled,
  variant = "strip",
  afterEntry,
  compact,
  activeChannel,
  onSelectChannel,
  className,
}: ConnectChooserProps) => {
  const end = secondaryColor || "#FF4DB8";
  const gradient = `linear-gradient(90deg, ${brandColor} 0%, ${end} 100%)`;
  const isGate = variant === "gate";
  const isMobile = useIsMobile(768);
  const Shell = isGate ? CardShell : ChipShell;
  // In the strip, compact mode hides detail lines; the gate always shows them.
  const showDetail = isGate || !compact;
  // Tab mode: strip variant + a channel-select handler. Voice/Chat/Phone/Email
  // report the chosen channel instead of acting immediately — the caller (the
  // live-agent page) swaps the stage beneath. QR stays a popover either way.
  const isTabsMode = !isGate && !!onSelectChannel;

  const [phoneOpen, setPhoneOpen] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [copiedField, setCopiedField] = useState<"phone" | "email" | "link" | null>(null);
  const phoneRef = useRef<HTMLDivElement>(null);
  const telCapable = isTelCapable();

  useEffect(() => {
    if (!phoneOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (phoneRef.current && !phoneRef.current.contains(e.target as Node)) setPhoneOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPhoneOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [phoneOpen]);

  useEffect(() => {
    if (!copiedField) return;
    const t = setTimeout(() => setCopiedField(null), 1800);
    return () => clearTimeout(t);
  }, [copiedField]);

  const handleCopy = async (field: "phone" | "email" | "link", value: string, message: string) => {
    if (await copyToClipboard(value, message)) setCopiedField(field);
  };

  // Tab mode: standard ARIA tabs arrow-key navigation (Left/Right, Up/Down for
  // the stacked mobile row, Home/End). Moves focus and, for real channels,
  // activates the tab immediately — QR is focusable but never auto-activates
  // (it stays a deliberate click to open the popover).
  const tablistRef = useRef<HTMLDivElement>(null);
  const handleTabsKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const navKeys = ["ArrowRight", "ArrowLeft", "ArrowDown", "ArrowUp", "Home", "End"];
    if (!navKeys.includes(e.key)) return;
    const container = tablistRef.current;
    if (!container) return;
    const tabs = Array.from(container.querySelectorAll<HTMLButtonElement>('[role="tab"]:not(:disabled)'));
    if (tabs.length === 0) return;
    const currentIndex = tabs.indexOf(document.activeElement as HTMLButtonElement);
    let nextIndex = currentIndex;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      nextIndex = currentIndex < 0 ? 0 : (currentIndex + 1) % tabs.length;
    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      nextIndex = currentIndex < 0 ? tabs.length - 1 : (currentIndex - 1 + tabs.length) % tabs.length;
    } else if (e.key === "Home") {
      nextIndex = 0;
    } else {
      nextIndex = tabs.length - 1;
    }
    e.preventDefault();
    const nextTab = tabs[nextIndex];
    nextTab.focus();
    const nextChannel = nextTab.dataset.channel as ConnectChannelKey | undefined;
    if (nextChannel && nextChannel !== "qr") {
      onSelectChannel?.(nextChannel);
      if (nextChannel === "voice") onStartVoice();
    }
  };

  // --- Channel nodes, shared by both layouts -------------------------------

  const voiceNode = isGate ? (
    // The hero: voice is what this Genie is for, so it gets the gradient and
    // the top slot. Same handler as the old standalone "Start talking" button.
    <button
      type="button"
      onClick={onStartVoice}
      disabled={disabled}
      className="group flex min-h-[72px] w-full items-center gap-3.5 rounded-2xl px-4 py-3.5 text-left text-white transition-opacity hover:opacity-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-genie-primary focus-visible:ring-offset-2 disabled:cursor-wait disabled:opacity-60"
      style={{ background: gradient, boxShadow: `0 14px 34px -12px ${brandColor}85` }}
      aria-label="Start a voice conversation"
    >
      <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-white/20">
        <GenieIcon name="microphone" size={22} color="#FFFFFF" />
      </span>
      <span className="flex min-w-0 flex-1 flex-col leading-tight">
        <span className="text-[16px] font-semibold">Talk to the Genie</span>
        <span className="mt-0.5 text-sm text-white/90">Speak in your language</span>
      </span>
      <ChevronRight
        size={20}
        strokeWidth={2.4}
        className="flex-shrink-0 opacity-90 transition-transform group-hover:translate-x-0.5"
      />
    </button>
  ) : isTabsMode ? (
    <TabPill
      channelKey="voice"
      disabled={disabled}
      onClick={() => {
        onSelectChannel?.("voice");
        onStartVoice();
      }}
      selected={activeChannel === "voice"}
      brandColor={brandColor}
      end={end}
      genieIcon="microphone"
      label="Voice"
      detail="Talk it through"
      aria-label="Start a voice conversation"
    />
  ) : (
    <ChipShell
      compact={compact}
      disabled={disabled}
      onClick={() => {
        onSelectChannel?.("voice");
        onStartVoice();
      }}
      brandColor={brandColor}
      end={end}
      genieIcon="microphone"
      label="Voice"
      detail={showDetail ? "Talk it through" : undefined}
      aria-label="Start a voice conversation"
    />
  );

  const chatNode = isTabsMode ? (
    <TabPill
      channelKey="chat"
      disabled={disabled}
      onClick={() => onSelectChannel!("chat")}
      selected={activeChannel === "chat"}
      brandColor={brandColor}
      end={end}
      genieIcon="chat"
      label="Chat"
      detail="Type instead"
      aria-label="Start a text chat"
    />
  ) : (
    <Shell
      compact={compact}
      disabled={disabled}
      onClick={onStartChat}
      brandColor={brandColor}
      end={end}
      genieIcon="chat"
      label={isGate ? "Type instead" : "Chat"}
      detail={showDetail ? (isGate ? "No microphone needed" : "Type instead") : undefined}
      aria-label="Start a text chat"
    />
  );

  const phoneNode = phoneNumber ? (
    isTabsMode ? (
      <TabPill
        channelKey="phone"
        disabled={disabled}
        onClick={() => onSelectChannel!("phone")}
        selected={activeChannel === "phone"}
        brandColor={brandColor}
        end={end}
        genieIcon="phone"
        label="Call"
        detail={phoneNumber}
        aria-label="Show call options"
      />
    ) : telCapable ? (
      <Shell
        compact={compact}
        disabled={disabled}
        href={`tel:${stripTel(phoneNumber)}`}
        onClick={() => trackCallClick("connect_chooser")}
        brandColor={brandColor}
        end={end}
        genieIcon="phone"
        label="Call"
        detail={showDetail ? phoneNumber : undefined}
        aria-label={`Call ${phoneNumber}`}
      />
    ) : (
      <div ref={phoneRef} className={isGate ? "relative" : "relative flex-shrink-0"}>
        <Shell
          compact={compact}
          disabled={disabled}
          onClick={() => setPhoneOpen((v) => !v)}
          brandColor={brandColor}
          end={end}
          genieIcon="phone"
          label="Call"
          detail={showDetail ? phoneNumber : undefined}
          aria-label={`Show phone number ${phoneNumber}`}
        />
        {phoneOpen && (
          <div
            role="dialog"
            aria-label="Phone number"
            className="absolute left-1/2 top-full z-[1650] mt-2 w-56 -translate-x-1/2 rounded-2xl border border-[#E9EDF3] bg-white p-3.5 text-center shadow-[0_18px_40px_-16px_rgba(15,23,42,0.35)]"
          >
            <p className="text-[15px] font-semibold text-[#222B45]">{phoneNumber}</p>
            <button
              type="button"
              onClick={() => void handleCopy("phone", phoneNumber, "Phone number copied")}
              className="mt-2.5 inline-flex min-h-[44px] w-full items-center justify-center gap-1.5 rounded-xl px-3 py-2 text-[12.5px] font-semibold text-white transition-opacity hover:opacity-90"
              style={{ background: gradient }}
            >
              {copiedField === "phone" ? <Check size={13} strokeWidth={2.5} /> : <Copy size={13} strokeWidth={2.25} />}
              {copiedField === "phone" ? "Copied" : "Copy number"}
            </button>
          </div>
        )}
      </div>
    )
  ) : null;

  const emailNode = emailAddress ? isTabsMode ? (
    <div className="flex min-w-0 items-center">
      <TabPill
        channelKey="email"
        disabled={disabled}
        onClick={() => onSelectChannel!("email")}
        selected={activeChannel === "email"}
        brandColor={brandColor}
        end={end}
        genieIcon="mail"
        label="Email"
        detail={emailAddress}
        aria-label="Show email options"
      />
      <span className="hidden min-[1000px]:inline-flex">
        <CopyEmailButton email={emailAddress} />
      </span>
    </div>
  ) : (
    <Shell
      compact={compact}
      disabled={disabled}
      href={`mailto:${emailAddress}`}
      brandColor={brandColor}
      end={end}
      genieIcon="mail"
      label="Email"
      detail={showDetail ? emailAddress : undefined}
      aria-label={`Email ${emailAddress}`}
      trailing={
        showDetail && (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void handleCopy("email", emailAddress, "Email address copied");
            }}
            disabled={disabled}
            className="ml-0.5 flex min-h-[44px] min-w-[44px] flex-shrink-0 items-center justify-center rounded-full p-2.5 text-gray-500 transition-colors hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50"
            aria-label="Copy email address"
            title="Copy email address"
          >
            {copiedField === "email" ? (
              <Check size={12} strokeWidth={2.5} className="text-genie-success" />
            ) : (
              <Copy size={12} strokeWidth={2.25} />
            )}
          </button>
        )
      }
    />
  ) : null;

  const qrNode = isTabsMode ? (
    <TabPill
      channelKey="qr"
      disabled={disabled}
      onClick={() => setQrOpen(true)}
      selected={false}
      brandColor={brandColor}
      end={end}
      genieIcon="qr-code"
      label="QR code"
      detail="Scan to open"
      aria-label="Show QR code for this Genie"
    />
  ) : (
    <Shell
      compact={compact}
      disabled={disabled}
      onClick={() => setQrOpen(true)}
      brandColor={brandColor}
      end={end}
      genieIcon="qr-code"
      label={isGate ? "Share this Genie" : "QR code"}
      detail={showDetail ? (isGate ? "Copy link or scan QR code" : "Scan to open") : undefined}
      aria-label="Show QR code for this Genie"
    />
  );

  // --- Layouts --------------------------------------------------------------

  const layout = isGate ? (
    <div className="flex flex-col gap-3">
      {voiceNode}
      {chatNode}
      <p className="text-center text-xs leading-relaxed text-gray-500">
        Your microphone starts only when you choose to talk.
      </p>
      {afterEntry}
      <details className="group/other mt-1">
        <summary className="flex min-h-[44px] cursor-pointer list-none items-center justify-center gap-2 rounded-xl text-sm text-gray-600 transition-colors hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-genie-primary [&::-webkit-details-marker]:hidden">
          More
          <ChevronDown size={16} className="transition-transform group-open/other:rotate-180" />
        </summary>
        <div className="mt-2 grid gap-2.5">
          {phoneNode}
          {emailNode}
          {qrNode}
        </div>
      </details>
    </div>
  ) : isTabsMode ? (
    // Tab bar: always icon tile + label, never icon-only. Desktop (>=768px)
    // is a labelled-pill row that's allowed to run wider than the ~620px
    // genie column — `md:left-1/2 md:-translate-x-1/2` re-centers it on the
    // column's own midpoint (the parent chain has no overflow-hidden closer
    // than the page's outer frame, which is comfortably wider than the
    // ~900px cap), so up to five labelled tabs read on one line instead of
    // shrinking to icon-only. Never wraps to a second row — wrapping pushes
    // the column's total height past the page's `overflow: hidden` wrapper
    // and clips the top row clean off — so this scrolls horizontally as a
    // fallback instead. Below 768px, TabPill itself collapses to a stacked
    // icon-over-label tile (see its own responsive classes).
    <div
      ref={tablistRef}
      role="tablist"
      aria-label="Choose how to connect"
      onKeyDown={handleTabsKeyDown}
      className="flex snap-x items-center gap-2.5 overflow-x-auto scrollbar-hidden pb-0.5 md:relative md:left-1/2 md:w-max md:max-w-[900px] md:-translate-x-1/2 md:justify-center"
    >
      {voiceNode}
      {chatNode}
      {phoneNode}
      {emailNode}
      {qrNode}
    </div>
  ) : (
    // Plain action row (no tabs): single row of equal chips, unchanged.
    <div className="flex items-center justify-center gap-2 overflow-x-auto scrollbar-hidden pb-0.5">
      {voiceNode}
      {chatNode}
      {phoneNode}
      {emailNode}
      {qrNode}
    </div>
  );

  return (
    <div className={className}>
      {layout}

      <BaseSheet
        isOpen={qrOpen}
        onClose={() => setQrOpen(false)}
        title="Share this Genie"
        description="Copy the link or scan the QR code to open this Genie on another device."
        size="md"
        side={isMobile ? "bottom" : "right"}
        overlayClassName="z-[11990]"
        contentClassName={cn("z-[12000] [&_button]:min-h-[44px] [&_button]:min-w-[44px]", isMobile ? "!max-h-[90dvh] rounded-t-2xl" : "h-[100dvh]")}
        footer={
          <button
            type="button"
            onClick={() => setQrOpen(false)}
            className="min-h-[44px] rounded-xl border border-genie-border px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Done
          </button>
        }
      >
          <div className="mx-auto w-full max-w-[320px] text-center">
            <div className="mt-4 flex items-center justify-center rounded-2xl border border-genie-border p-4">
              <QRCodeReact
                value={pageUrl}
                size={200}
                level="H"
                includeMargin={false}
                fgColor="#1A1A1A"
                imageSettings={
                  qrLogoUrl ? { src: qrLogoUrl, height: 30, width: 30, excavate: true } : undefined
                }
              />
            </div>
            <button
              type="button"
              onClick={() => void handleCopy("link", pageUrl, "Link copied to clipboard")}
              className="mt-4 inline-flex min-h-[44px] w-full items-center justify-center gap-1.5 rounded-xl px-4 py-2.5 text-[13px] font-semibold text-white transition-opacity hover:opacity-90"
              style={{ background: gradient }}
            >
              {copiedField === "link" ? <Check size={14} strokeWidth={2.5} /> : <Copy size={14} strokeWidth={2.25} />}
              {copiedField === "link" ? "Copied" : "Copy link"}
            </button>
          </div>
      </BaseSheet>
    </div>
  );
};

export default ConnectChooser;
